import { useRef } from 'react';
import { useQuery } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { handleError } from '../../../../../shared/ErrorCatching';
import { getImageProcessingInfo } from '../../imageprocessing/actions/ImageProcessingActions';
import * as Actions from '../Actions';
import { isDigitalStylingOrder, isPhotoOrder } from '../orderTypeChecks';
import { selectBasicOrderInfo } from '../Reducer';

export const basicOrderInfoQueryKey = 'basic-order-info';
export const imageProcessingInfoQueryKey = 'image-processing-info';

export const suspenseSafeCacheTime = 1000;

const queryBehavior = {
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    cacheTime: suspenseSafeCacheTime
};

type UseBasicOrderInfoDataProps = {
    orderId: number;
    onOrderLoaded: () => void;
};

type UseBasicOrderInfoDataResult = {
    reloadOrder: () => Promise<any>;
};

export function useBasicOrderInfoData(
    props: UseBasicOrderInfoDataProps
): UseBasicOrderInfoDataResult {
    const { orderId, onOrderLoaded } = props;
    const dispatch = useDispatch<any>();
    const order = useSelector(selectBasicOrderInfo);
    const lastReportedOrderId = useRef<number | null>(null);

    const viewQuery = useQuery(
        [basicOrderInfoQueryKey, orderId],
        () => dispatch(Actions.view(orderId)),
        {
            ...queryBehavior,
            onSuccess: () => {
                if (lastReportedOrderId.current === orderId) {
                    return;
                }
                lastReportedOrderId.current = orderId;
                onOrderLoaded();
            },
            onError: (error: any) => handleError(error)
        }
    );

    useQuery(
        [imageProcessingInfoQueryKey, orderId],
        () => dispatch(getImageProcessingInfo(orderId)),
        {
            ...queryBehavior,
            enabled: viewQuery.isSuccess && (isPhotoOrder(order) || isDigitalStylingOrder(order)),
            onError: (error: any) => handleError(error)
        }
    );

    const reloadOrder = (): Promise<any> => {
        return viewQuery.refetch();
    };

    return { reloadOrder };
}
